import { MessageSender, MessageType } from '../ChatMessages'
import type {
  ChatMessage,
  ProductItemType,
  ProductMessageItem,
} from '../ChatMessages'
import { ChatResponsePayload, ModelResponse, RawRecommendation } from './types'
import { createProxyApiUrl, getApiUrl } from '@moonpig/web-core-utils'
import type { ProductTileProduct } from '@moonpig/web-shared-products'

export const stripWrappingQuotes = (value: string): string => {
  if (value.length < 2) {
    return value
  }

  const wrappingDoubleQuotes = '"'
  const wrappingSingleQuotes = "'"
  const first = value[0]
  const last = value[value.length - 1]

  if (
    (first === wrappingDoubleQuotes && last === wrappingDoubleQuotes) ||
    (first === wrappingSingleQuotes && last === wrappingSingleQuotes)
  ) {
    return value.slice(1, -1)
  }

  return value
}

export const normalizeModelResponse = (outputText: unknown): string => {
  let normalized = String(outputText || '').trim()

  normalized = stripWrappingQuotes(normalized)

  const fencedJsonMatch = normalized.match(/```(?:json)?\s*([\s\S]*?)\s*```/i)

  if (fencedJsonMatch?.[1]) {
    normalized = fencedJsonMatch[1].trim()
  }

  return stripWrappingQuotes(normalized).trim()
}

export const parseModelJson = (value: unknown): ModelResponse | null => {
  if (value && typeof value === 'object') {
    return value as ModelResponse
  }

  if (typeof value !== 'string') {
    return null
  }

  try {
    return JSON.parse(normalizeModelResponse(value)) as ModelResponse
  } catch {
    return null
  }
}

export const extractRecommendationEntry = (
  recommendation: RawRecommendation,
): ProductMessageItem | null => {
  const keys = Object.keys(recommendation)
  if (keys.length === 0) {
    return null
  }

  const productType = keys[0]
  const normalizedCategory = productType.toLowerCase()
  if (
    !normalizedCategory.includes('card') &&
    !normalizedCategory.includes('gift') &&
    !normalizedCategory.includes('flower')
  ) {
    return null
  }

  const entry = recommendation[productType]

  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
    return null
  }

  const product = entry as Record<string, unknown>

  if (!('masterVariant' in product)) {
    return null
  }

  const mappedProduct = {
    ...product,
    category:
      'category' in product
        ? product.category
        : {
            name:
              typeof product.categoryName === 'string'
                ? product.categoryName
                : '',
            department:
              typeof product.categoryDepartment === 'string'
                ? product.categoryDepartment
                : '',
          },
  } as ProductTileProduct
  const normalizedProductType: ProductItemType = normalizedCategory.includes(
    'card',
  )
    ? 'cards'
    : 'gifts'

  return {
    ...mappedProduct,
    productType: normalizedProductType,
    title: mappedProduct.title,
    id: mappedProduct.id,
    price:
      typeof product.price === 'string'
        ? product.price.trim() || undefined
        : undefined,
    reason: typeof product.reason === 'string' ? product.reason : undefined,
    imageUrl:
      typeof product.image_url === 'string' ? product.image_url : undefined,
    productUrl:
      typeof product.product_url === 'string' ? product.product_url : undefined,
    imageQuery:
      typeof product.image_query === 'string' ? product.image_query : undefined,
  }
}

export const getRecommendations = (
  recommendations: ModelResponse['recommendations'],
) => {
  return Array.isArray(recommendations)
    ? recommendations
        .map(recommendation =>
          recommendation &&
          typeof recommendation === 'object' &&
          !Array.isArray(recommendation)
            ? extractRecommendationEntry(recommendation as RawRecommendation)
            : null,
        )
        .filter((recommendation): recommendation is ProductMessageItem =>
          Boolean(recommendation),
        )
    : []
}

export const getErrorMessages = (
  responsePayload: ChatResponsePayload,
): ChatMessage[] => {
  const errorMessage =
    typeof responsePayload.error === 'string'
      ? responsePayload.error.trim()
      : ''

  return errorMessage
    ? [
        {
          sender: MessageSender.Agent,
          type: MessageType.Text,
          content: errorMessage,
        },
      ]
    : []
}

export const getSuggestions = (model: ModelResponse): ChatMessage[] => {
  const suggestedReplies = Array.isArray(model.suggested_replies)
    ? model.suggested_replies
        .map(reply => String(reply || '').trim())
        .filter(Boolean)
    : []

  const followUpQuestion =
    typeof model.follow_up_question === 'string'
      ? model.follow_up_question.trim()
      : ''
  const supportUrl =
    typeof model.support_url === 'string' ? model.support_url.trim() : ''

  const messages: ChatMessage[] = []

  if (followUpQuestion) {
    messages.push({
      sender: MessageSender.Agent,
      type: MessageType.FollowUpQuestion,
      content: followUpQuestion,
    })
  }

  if (suggestedReplies.length > 0 || supportUrl) {
    messages.push({
      sender: MessageSender.Agent,
      type: MessageType.Suggestions,
      content: {
        title: 'Suggestions',
        suggestions: suggestedReplies,
        supportUrl: supportUrl || undefined,
        source: 'agent',
      },
    })
  }

  return messages
}

export const extractModelPayload = (payload: unknown): unknown => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return payload
  }

  const modelPayload = payload as Record<string, unknown>
  const response = modelPayload.response

  if (typeof response !== 'string') {
    return payload
  }

  const hasStructuredModelFields =
    'recommendations' in modelPayload ||
    'follow_up_question' in modelPayload ||
    'suggested_replies' in modelPayload ||
    'support_url' in modelPayload

  return hasStructuredModelFields ? payload : response
}

export const getModelOrFallback = (
  payload: unknown,
): { fallbackMessages: ChatMessage[]; model: ModelResponse | null } => {
  const responsePayload = (payload ?? {}) as ChatResponsePayload
  const agentPayload = responsePayload.agentResponse ?? payload
  const modelResponsePayload = extractModelPayload(agentPayload)
  const model = parseModelJson(modelResponsePayload)

  if (model) {
    return { fallbackMessages: [], model }
  }

  const fallback = normalizeModelResponse(modelResponsePayload)

  return {
    fallbackMessages: fallback
      ? [
          {
            sender: MessageSender.Agent,
            type: MessageType.Text,
            content: fallback,
          },
        ]
      : [],
    model: null,
  }
}

export const getAgentResponse = (model: ModelResponse): ChatMessage[] => {
  const responseText =
    typeof model.response === 'string' ? model.response.trim() : ''

  return responseText
    ? [
        {
          sender: MessageSender.Agent,
          type: MessageType.Text,
          content: responseText,
        },
      ]
    : []
}

export const getConciergeChatUrl = (): string => {
  const chatUrl = `${getApiUrl()}/concierge/chat`
  const url = createProxyApiUrl(chatUrl)
  return url
}

export const getNextSessionId = (
  payload: ChatResponsePayload,
): string | null => {
  const { runtimeSessionId, agentResponse } = payload

  // 1. Prioritize direct session IDs
  if (runtimeSessionId) return runtimeSessionId

  // 2. Fallback to agentResponse if it's a valid object with a sessionId
  if (
    agentResponse &&
    typeof agentResponse === 'object' &&
    !Array.isArray(agentResponse)
  ) {
    const nestedId = (agentResponse as Record<string, unknown>).sessionId
    if (typeof nestedId === 'string') return nestedId
  }

  return null
}
